import type { ComponentProps } from 'react';
import { useMemo, useContext } from 'react';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import type { ContestType } from '@betterpool/api-types/contests-service';

import type { LeagueAlias } from '~/domains/common/enums/league-alias.enum';
import { Routes } from '~/domains/common/constants/routes';
import { URLQueryKeys } from '~/domains/common/types/URLQueryKeys';
import { RootModalsContext } from '~/domains/common/context/RootModalsContext/RootModalsContext';
import { UserContext } from '~/components/providers/UserProvider';

import useIsCommissioner from '../../../utils/useIsCommissioner';
import { useAutoPickStrategy } from '~/domains/contest/domains/common/hooks/useAutoPickStrategy';

import useGamecenterHeader from '../../../hooks/useGamecenterHeader';
import { ConfirmationModalType } from '../../../context/GamecenterConfirmationModalContext/types';
import { ConfirmationModalContext } from '../../../context/GamecenterConfirmationModalContext/ConfirmationModalContext';
import { useContestContext } from '../../../context/ContestContext/ContestContext';

import type GamecenterPicksHeader from './GamecenterPicksHeader';

function useGamecenterPicksHeader({
  canMakePicks = true,
  entryName,
  expectedPicksCount,
  hadPicks,
  isAccessForbidden,
  isPristine,
  onEntryWithdrawClick,
  currentUserEntry,
}: {
  canMakePicks?: boolean;
  entryName?: string;
  expectedPicksCount: number;
  hadPicks?: boolean;
  isAccessForbidden?: boolean;
  isPristine?: boolean;
  onEntryWithdrawClick?: () => void;
  currentUserEntry?: { user?: { id: string } };
}) {
  const { t } = useTranslation('contest');
  const router = useRouter();
  const { user } = useContext(UserContext);
  const { contest } = useContestContext();

  // Get all commissioner-related state
  const {
    isCommissionerMode,
    isLoggedInUserTheCommissioner,
    isSelfManagedContest,
    contestUser,
    shouldAllowCommissionerAccess,
  } = useIsCommissioner();
  const baseCloseHref = useGamecenterHeader().closeHref;

  // Determine the correct closeHref based on commissioner mode
  const closeHref = (() => {
    if (shouldAllowCommissionerAccess && contestUser) {
      // Commissioner mode: route back to entries-manager with contestUser parameter (from URL)
      return Routes.entrantsManager(contest.id, { [URLQueryKeys.CONTEST_USER]: contestUser });
    }
    return baseCloseHref;
  })();

  const confirmationModal = useContext(ConfirmationModalContext);
  const { openModal } = useContext(RootModalsContext);
  const isMultiSlate = contest.slates.length > 1;

  const autoPicksSettings = contest.settings?.autoPicksSettings;
  const hasAutoPicksEnabled =
    autoPicksSettings?.maxAutoPicks === null || autoPicksSettings?.maxAutoPicks > 0;

  const { data: currentAutoPickStrategy } = useAutoPickStrategy(contest.id, hasAutoPicksEnabled);

  const handleEditAutoPicks = useMemo(() => {
    if (!hasAutoPicksEnabled) return undefined;

    return () => {
      const availableStrategies = autoPicksSettings?.availableStrategies || [];
      const defaultStrategy =
        currentAutoPickStrategy?.strategy ||
        autoPicksSettings?.strategies?.[0] ||
        availableStrategies[0]?.value;

      openModal({
        type: 'AutoPickEditSheet',
        contestId: contest.id,
        currentStrategy: defaultStrategy,
        availableStrategies,
        title: t('gamecenter.modal.gamecenterAutoPickEditSheet.editTitle'),
        description: t('gamecenter.modal.gamecenterAutoPickEditSheet.editDescription'),
      });
    };
  }, [
    hasAutoPicksEnabled,
    autoPicksSettings,
    currentAutoPickStrategy?.strategy,
    contest.id,
    openModal,
    t,
  ]);

  const handleOnCloseClick = useMemo(() => {
    if (isPristine || isAccessForbidden) {
      return;
    }

    return (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      confirmationModal.setIsOpen({
        isOpen: true,
        payload: {
          type: ConfirmationModalType.DISCARD_PICKS_CHANGES,
          onClose: () => {
            confirmationModal.setIsOpen({ isOpen: false });
          },
          onConfirm: () => {
            confirmationModal.setIsOpen({ isOpen: false });
            router.push(closeHref);
          },
        },
      });
    };
  }, [isPristine, isAccessForbidden, confirmationModal, router, closeHref]);

  const isMenuVisible = !!onEntryWithdrawClick || hasAutoPicksEnabled;

  return {
    closeHref,
    title: hadPicks
      ? t(
          canMakePicks
            ? 'gamecenter.common.editPicks__hasPlural'
            : 'gamecenter.common.yourPicks__hasPlural',
          {
            count: isMultiSlate ? 2 : expectedPicksCount,
          }
        )
      : contest.name,
    subtitle: entryName,
    isMenuVisible,
    onCloseClick: handleOnCloseClick,
    onEntryWithdrawClick,
    leagueAlias: contest.league.alias as LeagueAlias,
    contestType: contest.contest_type as ContestType,
    hasAutoPicksEnabled,
    onEditAutoPicks: handleEditAutoPicks,
  } satisfies ComponentProps<typeof GamecenterPicksHeader>;
}

export default useGamecenterPicksHeader;
